import { useSystemThemeMode } from "@hooks/use-system-theme-mode";
import { appStateSelector } from "@store/app/app-selector";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { useAppSelector } from "@store/store-helper";
/**
 * The colour-scheme mode the app should be in, `"system"` included.
 *
 * This is the value MUI's `setMode` takes, so handing it over unresolved lets MUI track the OS
 * query itself instead of the app resolving it and MUI resolving it again.
 *
 * Resolution order:
 * 1. An explicit "light" or "dark" `theme_mode` setting is always respected.
 * 2. "system", or no setting at all, follows the OS only with beta testing enabled. Without beta
 *    it stays light, which was the default before dark mode existed.
 */
export function useEffectiveColorSchemeMode() {
  const {
    isBetaTestingEnabled
  } = useAppSelector(appStateSelector);
  const {
    theme_mode: themeModeSetting
  } = useAppSelector(userSettingsSelector);
  const themeMode = themeModeSetting?.value ?? "system";
  if (themeMode === "system" && !isBetaTestingEnabled) {
    return "light";
  }
  return themeMode;
}

/**
 * The same mode with `"system"` resolved against the OS colour scheme, for the places that need a
 * concrete answer rather than a mode to hand to MUI.
 */
export function useEffectiveThemeMode() {
  const mode = useEffectiveColorSchemeMode();
  const systemThemeMode = useSystemThemeMode();
  return mode === "system" ? systemThemeMode : mode;
}